import React, { useEffect, useState, useMemo } from 'react';
import { cn } from '@/shared/utils/cn';
import { CardsWithAsideProps, CardsWithAsideTabContentProps } from '../../types';
import { Button } from '@/modules/Button';
import { Tooltip } from '@/modules/Tooltip';
import { Popup } from '@/modules/Popup';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { TooltipPosition } from '@/shared/types/tooltip';
import { VisualSlotType, AnyIconDefinition } from '@/shared/types/types';

type TabAction = {
  id?: string;
  tooltip?: string;
  tooltipPosition?: TooltipPosition;
  component?: React.ReactNode;
  icon?: VisualSlotType | AnyIconDefinition;
};

export const CardsWithAsideComponent = ({
  children,
  activeCard,
  InitialContent,
  className,
  moreOptions = false,
  onClickMoreOptions,
  popup,
  width = '377px',
  height = '218px',
  showTooltips = true,
  tooltipPosition: globalTooltipPosition = 'right',
}: CardsWithAsideProps) => {
  const [activeTabCard, setActiveTabCard] = useState<string | null>(activeCard ?? null);

  useEffect(() => {
    setActiveTabCard(activeCard ?? null);
  }, [activeCard]);

  // Procesar children para construir el mapa de acciones y encontrar el tab activo
  const { actions, activeTabContent } = useMemo(() => {
    const actionsMap: Record<string, TabAction> = {};
    let activeContent: React.ReactNode = null;

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const props = child.props as CardsWithAsideTabContentProps;

        if (props.Component) {
          // Si este child tiene un componente asociado
          actionsMap[props.id] = {
            component: props.Component,
            tooltip: props.tooltip,
            tooltipPosition: props.tooltipPosition,
          };
        } else if (props.icon) {
          // Si es un ícono, guardamos el id y el tooltip
          const iconKey = typeof props.icon === 'string' ? props.icon : props.id || 'icon';
          actionsMap[iconKey] = {
            id: props.id,
            tooltip: props.tooltip,
            tooltipPosition: props.tooltipPosition,
            icon: props.icon as VisualSlotType | AnyIconDefinition,
          };
        }

        // Si este es el tab activo, guardamos su contenido
        if (props.id === activeTabCard) {
          activeContent = (
            <div
              key={props.id}
              className={cn(
                'custom-scrollbar ui-h-full ui-w-full ui-overflow-y-auto',
                props.className
              )}
              style={props.style}
            >
              {child}
            </div>
          );
        }
      }
    });

    return { actions: actionsMap, activeTabContent: activeContent };
  }, [children, activeTabCard]);

  const handleTabClick = (actionId: string | undefined) => {
    if (!actionId) return;
    setActiveTabCard(actionId === activeTabCard ? null : actionId);
  };

  const containerWidth = typeof width === 'number' ? `${width}px` : width;
  const containerHeight = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={cn('ui-relative ui-overflow-hidden ui-rounded-lg ui-shadow-02', className)}
      style={{
        width: containerWidth,
        height: containerHeight,
        zIndex: 1,
      }}
    >
      {/* Panel lateral con tabs */}
      <div
        className={cn(
          'ui-absolute ui-right-0 ui-top-0 ui-flex ui-h-full ui-justify-end',
          'ui-transition-all ui-duration-300 ui-ease-in-out'
        )}
        style={{
          width: activeTabCard ? '100%' : '44px',
          zIndex: 1,
        }}
      >
        {/* Panel de contenido del tab activo */}
        <div
          className={cn(
            'ui-h-full ui-bg-white ui-transition-all ui-duration-300 ui-ease-in-out',
            'ui-overflow-hidden'
          )}
          style={{
            width: activeTabCard ? '100%' : 0,
            zIndex: 1,
            transformOrigin: activeTabCard ? 'right' : 'left',
          }}
        >
          {activeTabContent}
        </div>

        {/* Barra lateral con botones */}
        <div
          className="ui-relative ui-z-10 ui-h-full ui-w-11 ui-min-w-11 ui-bg-fill"
          style={{ zIndex: 10 }}
        >
          <div className="no-scrollbar ui-mb-auto ui-flex ui-h-[calc(100%)] ui-flex-col ui-items-center ui-justify-between ui-gap-8 ui-overflow-y-auto ui-py-8">
            {/* Botón de opciones adicionales */}
            {moreOptions && (
              <>
                {popup ? (
                  <Popup
                    position="left"
                    icon={faEllipsis}
                    color="secondary"
                    size="xs"
                    pill="xs"
                    tooltip={showTooltips ? 'Más opciones' : undefined}
                    tooltipPosition={globalTooltipPosition}
                    closeOnClickOutside
                    closeOnEscape
                  >
                    {popup}
                  </Popup>
                ) : (
                  <Tooltip
                    label="Más opciones"
                    position={globalTooltipPosition}
                    disabled={!showTooltips}
                  >
                    <Button
                      color="secondary"
                      size="xs"
                      pill="xs"
                      icon={faEllipsis}
                      onClick={onClickMoreOptions}
                    />
                  </Tooltip>
                )}
              </>
            )}

            {/* Botones de tabs */}
            <div className="ui-flex ui-flex-1 ui-flex-col ui-items-center ui-justify-center ui-gap-8">
              {Object.entries(actions).map(([key, action]) => {
                if (action.component) {
                  // Si tenemos un componente, lo renderizamos directamente
                  return <React.Fragment key={key}>{action.component}</React.Fragment>;
                } else {
                  // Si no hay component, significa que tenemos un id y un tooltip
                  const isActive = activeTabCard === action.id;
                  const iconName = action.icon;

                  // Determinar la posición del tooltip (individual > global)
                  const tooltipPos = action.tooltipPosition || globalTooltipPosition;

                  return (
                    <Tooltip
                      key={key}
                      label={action.tooltip || ''}
                      position={tooltipPos}
                      disabled={!showTooltips || !action.tooltip}
                    >
                      <Button
                        color={isActive ? 'primary' : 'secondary'}
                        size="xs"
                        pill="xs"
                        icon={iconName}
                        onClick={() => handleTabClick(action.id)}
                      />
                    </Tooltip>
                  );
                }
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Contenido inicial - debe estar después del panel lateral para que no se oculte */}
      {InitialContent && (
        <div
          className="ui-absolute ui-inset-0 ui-h-full ui-w-full"
          style={{
            paddingRight: '44px', // Espacio para la barra lateral de botones
            zIndex: 0,
          }}
        >
          {InitialContent}
        </div>
      )}
    </div>
  );
};

/**
 * Componente TabContent para usar dentro de CardsWithAside
 */
export const TabContent = ({
  children,
  id,
  icon,
  tooltip,
  Component,
  className,
  style,
  ...props
}: CardsWithAsideTabContentProps) => {
  return (
    <div className={cn('ui-h-full ui-w-full', className)} style={style} {...props}>
      {children}
    </div>
  );
};

// Asignar TabContent como subcomponente
CardsWithAsideComponent.TabContent = TabContent;
